import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../contexts/UserContext";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useMetaDescription } from "../../hooks/useMetaDescription";
import { useHistory, Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import api from "../../services/api";
import toast from "react-hot-toast";
import { toastDefsError } from "../../config/toastConfig";
import { IconProfileButtonDown, IconProfileButtonUp, IconPlus, IconAdvancePagination, IconBackPagination } from "../../components/icons";
import { CreditCard, ExternalLink } from "lucide-react";
import { comum } from "../../styles/Themes/light";
import { Paginate } from "../Proposals/styles";
import PlanosTabs from "../../components/Ui/PlanosTabs";
import PlanosFilter from "../../components/Ui/PlanosFilter";
import PlanosSkeleton from "../../components/Ui/PlanosSkeleton";
import Tooltip from "../../components/Ui/Tooltip/index";
import ModalNovoPlano from "../../components/Planos/ModalNovoPlano";
import { applyMoneyMask } from "../../utils/masks";
import {
    Container,
    Menu,
    PrimaryButton,
    SecondaryButton,
    TableContainer,
    PlanoCard,
    PlanoCardDesktop,
    PlanoCardMobile,
    PlanoCardMobileTop,
    PlanoCardMobileRow,
    PlanoCardMobileDetails,
    PlanoCardMobileDetailItem,
    PlanoCardMobileActions,
    PlanoWrapper,
    PlanoTop,
    PlanoBottom,
    DivIcon,
    DivIconSpacer,
    DivCol,
    DivActionsRight,
    DivArrow,
    StatusBadge,
    LinkAbrir,
    PaginationWrapper,
} from "./styles";

const BREAKPOINT_USE_PAGE_ACTIONS = 992;

function useNarrowViewport() {
    const [narrow, setNarrow] = useState(
        typeof window !== "undefined" ? window.matchMedia(`(max-width: ${BREAKPOINT_USE_PAGE_ACTIONS}px)`).matches : true
    );
    useEffect(() => {
        const mql = window.matchMedia(`(max-width: ${BREAKPOINT_USE_PAGE_ACTIONS}px)`);
        const handler = () => setNarrow(mql.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, []);
    return narrow;
}

interface PlanoItem {
    id_plano_solicitante_template: number;
    nome: string;
    quantidade_creditos: number | null;
    valor_plano: number | null;
    valor_onboarding: number | null;
    validade_creditos_dias: number | null;
    status: string;
    ordem_servico?: number;
    qtd_servicos?: number;
}

interface Contagem {
    todos: number;
    ativos: number;
    inativos: number;
}

function isPlanoOS(plano: PlanoItem): boolean {
    return plano.ordem_servico === 1;
}

export default function Planos() {
    useDocumentTitle({ title: "Planos" });
    useMetaDescription({
        description: "Gerencie os planos e precificação de serviços na EasyJur Legal Ops.",
    });

    const history = useHistory();
    const { user, setPageName, setPageType } = useAuth();
    const [loading, setLoading] = useState(true);
    const [planos, setPlanos] = useState<PlanoItem[]>([]);
    const [contagem, setContagem] = useState<Contagem>({ todos: 0, ativos: 0, inativos: 0 });
    const [selectedTab, setSelectedTab] = useState<string>("todos");
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [openModalNovo, setOpenModalNovo] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage] = useState(10);
    const [mounted, setMounted] = useState(false);
    const narrowViewport = useNarrowViewport();

    useEffect(() => {
        setMounted(true);
    }, []);

    const totalPages = Math.ceil(planos.length / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPlanos = planos.slice(startIndex, endIndex);

    const isAdmin = user?.profile?.toLowerCase() === "admin";
    const isOperador = user?.profile?.toLowerCase() === "operador";

    useEffect(() => {
        if (!isAdmin && !isOperador) {
            history.replace("/demandas");
            return;
        }
        setPageName("Planos");
        setPageType("planos");
    }, [isAdmin, isOperador, history, setPageName, setPageType]);

    async function fetchPlanos() {
        try {
            setLoading(true);
            const status = selectedTab === "todos" ? "todos" : selectedTab === "ativos" ? "ativo" : "inativo";
            const params = new URLSearchParams();
            if (status !== "todos") params.set("status", status);
            if (searchTerm.trim()) params.set("q", searchTerm.trim());
            const url = `/plano-solicitante-template${params.toString() ? "?" + params.toString() : ""}`;
            const res = await api.get(url);
            if (res.data?.status !== "success") {
                throw new Error(res.data?.message || "Erro ao carregar planos");
            }
            const lista = Array.isArray(res.data?.result) ? res.data.result : [];
            lista.sort((a: PlanoItem, b: PlanoItem) => {
                const va = a.quantidade_creditos != null ? a.quantidade_creditos : Number.MAX_SAFE_INTEGER;
                const vb = b.quantidade_creditos != null ? b.quantidade_creditos : Number.MAX_SAFE_INTEGER;
                return va - vb;
            });
            setPlanos(lista);
            const c = res.data?.contagem;
            setContagem({
                todos: c?.todos ?? res.data.result?.length ?? 0,
                ativos: c?.ativos ?? 0,
                inativos: c?.inativos ?? 0,
            });
        } catch (err: any) {
            toast.error(err.response?.data?.message || err.message || "Erro ao carregar planos", toastDefsError);
            setPlanos([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!isAdmin && !isOperador) return;
        fetchPlanos();
    }, [selectedTab, searchTerm, isAdmin, isOperador]);

    useEffect(() => {
        setCurrentPage(0);
    }, [selectedTab, searchTerm]);

    const handlePageChange = (selectedItem: { selected: number }) => {
        setCurrentPage(selectedItem.selected);
    };

    const toggleExpanded = (id: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    if (!isAdmin && !isOperador) {
        return null;
    }

    const headerActions =
        (isAdmin || isOperador) && (
            <>
                <SecondaryButton
                    type="button"
                    onClick={() => history.push("/servicos-template")}
                >
                    <CreditCard size={19} strokeWidth={2} />
                    Template de serviços
                </SecondaryButton>
                {isAdmin && (
                    <PrimaryButton
                        type="button"
                        onClick={() => setOpenModalNovo(true)}
                    >
                        <IconPlus />
                        Novo plano
                    </PrimaryButton>
                )}
            </>
        );

    const portalTarget =
        !narrowViewport && mounted && typeof document !== "undefined"
            ? document.getElementById("planos-header-actions")
            : null;

    return (
        <Container>
            {portalTarget ? createPortal(headerActions, portalTarget) : (isAdmin || isOperador) ? (
                <div className="planos-top-actions">{headerActions}</div>
            ) : null}

            <Menu>
                <PlanosTabs
                    selected={selectedTab}
                    setSelected={setSelectedTab}
                    todos={contagem.todos}
                    ativos={contagem.ativos}
                    inativos={contagem.inativos}
                />
                <PlanosFilter searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </Menu>

            {loading && <PlanosSkeleton />}

            {!loading && planos.length === 0 && (
                <div className="zero">Nenhum plano encontrado.</div>
            )}

            {!loading && planos.length > 0 && (
                <TableContainer>
                    {currentPlanos.map((plano) => {
                            const id = String(plano.id_plano_solicitante_template);
                            const os = isPlanoOS(plano);
                            const expanded = expandedRows.has(id);
                            const creditsLabel = os ? "-" : (plano.quantidade_creditos != null && plano.quantidade_creditos !== 0 ? `${plano.quantidade_creditos} créditos` : "-");
                            const valorLabel = os ? "-" : (plano.valor_plano != null && Number(plano.valor_plano) !== 0 ? applyMoneyMask(plano.valor_plano) : "-");
                            const validadeLabel = os ? "-" : (plano.validade_creditos_dias != null && plano.validade_creditos_dias !== 0 ? `${plano.validade_creditos_dias} dias` : "-");
                            const onboardingLabel = os ? "-" : (plano.valor_onboarding != null && plano.valor_onboarding !== 0 ? applyMoneyMask(plano.valor_onboarding) : "-");
                            return (
                                <PlanoCard key={id}>
                                    <PlanoCardDesktop>
                                        <PlanoWrapper menuOpen={expanded}>
                                            <PlanoTop>
                                                <DivIcon>
                                                    <CreditCard size={20} strokeWidth={2} />
                                                </DivIcon>
                                                <DivCol className="plano-name">
                                                    <div className="title">Plano:</div>
                                                    <Tooltip content={plano.nome || ""} longContent centerAboveTrigger alignAbove="start">
                                                        <div className="subtitle">{plano.nome || "-"}</div>
                                                    </Tooltip>
                                                </DivCol>
                                                <DivCol width="100px">
                                                    <div className="title">Créditos:</div>
                                                    <div className="subtitle">{creditsLabel}</div>
                                                </DivCol>
                                                <DivCol width="120px">
                                                    <div className="title">Valor:</div>
                                                    <div className="subtitle">{valorLabel}</div>
                                                </DivCol>
                                                <DivCol width="100px">
                                                    <div className="title">Solicitantes:</div>
                                                    <div className="subtitle">-</div>
                                                </DivCol>
                                                <DivCol width="90px" className="status-col">
                                                    <StatusBadge className={plano.status === "ativo" ? "success" : "default"}>
                                                        {plano.status === "ativo" ? "Ativo" : "Inativo"}
                                                    </StatusBadge>
                                                </DivCol>
                                                <DivActionsRight>
                                                    <LinkAbrir as={Link} to={`/planos/${plano.id_plano_solicitante_template}`} title="Abrir">
                                                        <ExternalLink size={18} color="var(--ink-white)" />
                                                    </LinkAbrir>
                                                    <DivArrow onClick={() => toggleExpanded(id)}>
                                                        {expanded ? <IconProfileButtonUp /> : <IconProfileButtonDown />}
                                                    </DivArrow>
                                                </DivActionsRight>
                                            </PlanoTop>
                                            <PlanoBottom menuOpen={expanded}>
                                                <DivIconSpacer />
                                                <DivCol width="180px">
                                                    <div className="title">Validade (dias):</div>
                                                    <div className="subtitle">{validadeLabel}</div>
                                                </DivCol>
                                                <DivCol width="180px" minWidth="140px">
                                                    <div className="title">Serviços Configurados:</div>
                                                    <div className="subtitle">{plano.qtd_servicos != null ? String(plano.qtd_servicos) : "-"}</div>
                                                </DivCol>
                                                <DivCol width="120px">
                                                    <div className="title">Onboarding:</div>
                                                    <div className="subtitle">{onboardingLabel}</div>
                                                </DivCol>
                                            </PlanoBottom>
                                        </PlanoWrapper>
                                    </PlanoCardDesktop>

                                    <PlanoCardMobile>
                                        <PlanoCardMobileTop>
                                            <div className="mobile-icon">
                                                <CreditCard size={24} strokeWidth={2} />
                                            </div>
                                            <div className="mobile-name-credits">
                                                <span className="mobile-name" title={plano.nome && plano.nome.trim() ? plano.nome : undefined}>{plano.nome || "-"}</span>
                                                <span className="mobile-credits">{creditsLabel}</span>
                                            </div>
                                            <div className="mobile-badge-wrap">
                                                <StatusBadge className={plano.status === "ativo" ? "success" : "default"}>
                                                    {plano.status === "ativo" ? "Ativo" : "Inativo"}
                                                </StatusBadge>
                                            </div>
                                        </PlanoCardMobileTop>
                                        <PlanoCardMobileRow>
                                            <div className="mobile-title">Valor</div>
                                            <div className="mobile-value">{valorLabel}</div>
                                        </PlanoCardMobileRow>
                                        <PlanoCardMobileRow>
                                            <div className="mobile-title">Solicitantes</div>
                                            <div className="mobile-value">-</div>
                                        </PlanoCardMobileRow>
                                        {expanded && (
                                            <PlanoCardMobileDetails>
                                                <PlanoCardMobileDetailItem>
                                                    <div className="mobile-title">Validade</div>
                                                    <div className="mobile-value">{validadeLabel}</div>
                                                </PlanoCardMobileDetailItem>
                                                <PlanoCardMobileDetailItem>
                                                    <div className="mobile-title">Serviços</div>
                                                    <div className="mobile-value">{plano.qtd_servicos != null ? String(plano.qtd_servicos) : "-"}</div>
                                                </PlanoCardMobileDetailItem>
                                                <PlanoCardMobileDetailItem>
                                                    <div className="mobile-title">Onboarding</div>
                                                    <div className="mobile-value">{onboardingLabel}</div>
                                                </PlanoCardMobileDetailItem>
                                            </PlanoCardMobileDetails>
                                        )}
                                        <PlanoCardMobileActions>
                                            <Link className="link-abrir-mobile" to={`/planos/${plano.id_plano_solicitante_template}`}>
                                                <ExternalLink size={18} color="var(--ink-white)" /> Abrir
                                            </Link>
                                            <button type="button" className="arrow-btn-mobile" onClick={() => toggleExpanded(id)} aria-label={expanded ? "Recolher" : "Expandir"}>
                                                {expanded ? <IconProfileButtonUp /> : <IconProfileButtonDown />}
                                            </button>
                                        </PlanoCardMobileActions>
                                    </PlanoCardMobile>
                                </PlanoCard>
                            );
                        })}
                </TableContainer>
            )}

            {!loading && totalPages > 1 && (
                <PaginationWrapper>
                    <Paginate>
                        <ReactPaginate
                            breakLabel="..."
                            nextLabel={<IconAdvancePagination color={comum.colors.interfaceTextGray} />}
                            previousLabel={<IconBackPagination color={comum.colors.interfaceTextGray} />}
                            onPageChange={handlePageChange}
                            pageRangeDisplayed={1}
                            marginPagesDisplayed={1}
                            pageCount={totalPages}
                            forcePage={currentPage}
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="back-page"
                            previousLinkClassName="back-link-page"
                            nextClassName="next-page"
                            nextLinkClassName="next-link-page"
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            containerClassName="pagination"
                            activeClassName="active"
                            activeLinkClassName="active"
                        />
                    </Paginate>
                </PaginationWrapper>
            )}

            <ModalNovoPlano
                open={openModalNovo}
                onClose={() => setOpenModalNovo(false)}
                onSuccess={fetchPlanos}
            />
        </Container>
    );
}
